'use client'

import * as SliderPrimitive from '@radix-ui/react-slider'
import { TriangleAlertIcon } from 'lucide-react'
import { type ComponentProps, useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '../../utils/cn'

const DEFAULT_WARNING_THRESHOLD = 85
const BUFFER_ZONE = 2
const KEYBOARD_TOOLTIP_TIMEOUT_MS = 1000
const KEYBOARD_STEP_SIZE = 5
const SHIFT_STEP_SIZE = 25
const ARROW_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'])

function getRangeBackgroundClass(disabled: boolean | undefined, isInWarningZone: boolean): string {
  if (disabled) {
    return isInWarningZone ? 'bg-destructive' : 'bg-primary/40'
  }
  if (isInWarningZone) return 'bg-destructive-foreground'
  return 'bg-primary'
}

type SliderProps = Omit<ComponentProps<typeof SliderPrimitive.Root>, 'value' | 'onValueChange'> & {
  value?: number
  onValueChange?: (value: number) => void
  warningThreshold?: number
}

function Slider({
  className,
  value = 0,
  onValueChange,
  warningThreshold = DEFAULT_WARNING_THRESHOLD,
  disabled,
  ...props
}: SliderProps) {
  const [isInWarningZone, setIsInWarningZone] = useState(value >= warningThreshold)
  const [showKeyboardTooltip, setShowKeyboardTooltip] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const keyboardTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Handle warning zone with hysteresis buffer
  // Clamp exit threshold to 0 to prevent negative values when warningThreshold < BUFFER_ZONE
  useEffect(() => {
    if (isInWarningZone) {
      // Already in warning zone - only exit if below threshold - buffer
      const exitThreshold = Math.max(0, warningThreshold - BUFFER_ZONE)
      if (value < exitThreshold) {
        setIsInWarningZone(false)
      }
    } else {
      // Not in warning zone - only enter if at or above threshold
      if (value >= warningThreshold) {
        setIsInWarningZone(true)
      }
    }
  }, [value, warningThreshold, isInWarningZone])

  const handleValueChange = useCallback(
    (values: number[]) => {
      onValueChange?.(values[0] ?? 0)
    },
    [onValueChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!ARROW_KEYS.has(e.key)) return

      setShowKeyboardTooltip(true)

      if (keyboardTooltipTimeoutRef.current) {
        clearTimeout(keyboardTooltipTimeoutRef.current)
      }
      keyboardTooltipTimeoutRef.current = setTimeout(() => {
        setShowKeyboardTooltip(false)
      }, KEYBOARD_TOOLTIP_TIMEOUT_MS)

      // Always handle keyboard navigation manually to enforce custom step sizes
      e.preventDefault()
      const isIncrement = e.key === 'ArrowRight' || e.key === 'ArrowUp'
      const stepSize = e.shiftKey ? SHIFT_STEP_SIZE : KEYBOARD_STEP_SIZE
      const step = isIncrement ? stepSize : -stepSize
      const newValue = Math.max(0, Math.min(100, value + step))
      onValueChange?.(newValue)
    },
    [value, onValueChange]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (keyboardTooltipTimeoutRef.current) {
        clearTimeout(keyboardTooltipTimeoutRef.current)
      }
    }
  }, [])

  const showTooltip = isHovering || showKeyboardTooltip || (isInWarningZone && isDragging)
  const tooltipContent = isInWarningZone ? (
    <div className="text-destructive-foreground flex items-center gap-1">
      <TriangleAlertIcon className="size-3" />
      <span>{value}%</span>
    </div>
  ) : (
    `${value}%`
  )

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      className={cn('relative flex w-full touch-none items-center select-none', className)}
      value={[value]}
      onValueChange={handleValueChange}
      onPointerDown={() => setIsDragging(true)}
      onPointerUp={() => setIsDragging(false)}
      step={1}
      disabled={disabled}
      {...props}
    >
      <SliderPrimitive.Track className="bg-accent relative h-2 w-full grow overflow-hidden rounded-full">
        <SliderPrimitive.Range
          className={cn(
            'absolute h-full transition-colors',
            getRangeBackgroundClass(disabled, isInWarningZone)
          )}
        />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        className="bg-primary-foreground ring-offset-background focus-visible:ring-ring border-border relative block size-[18px] cursor-grab rounded-full border-[0.5px] shadow-md transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none active:cursor-grabbing disabled:pointer-events-none disabled:opacity-50"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onKeyDown={handleKeyDown}
        style={{ touchAction: 'none' }}
      >
        {showTooltip && (
          <div className="bg-card absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 rounded-md border px-2 py-1 text-xs shadow-md">
            {tooltipContent}
          </div>
        )}
      </SliderPrimitive.Thumb>
    </SliderPrimitive.Root>
  )
}

export { Slider }
export type { SliderProps }
